import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";

interface SlideshowAudioControlProps {
  muted: boolean;
  onToggleMute: () => void;
}

/**
 * Local, per-screen video audio toggle for the slideshow — mute on/off only,
 * no level. Purely presentational; the page owns `muted` and defaults it to
 * true (every screen is silent until someone taps to turn sound on). Styled to
 * match the sibling fullscreen / exit controls in the tap-to-reveal layer.
 */
function SlideshowAudioControlImpl({ muted, onToggleMute }: SlideshowAudioControlProps) {
  const { t } = useTranslation();
  const label = muted ? t("slideshow.unmute") : t("slideshow.mute");

  return (
    <button
      type="button"
      onClick={onToggleMute}
      aria-label={label}
      aria-pressed={!muted}
      title={label}
      className="flex flex-col items-center gap-2 cursor-pointer group"
    >
      <span className="flex items-center justify-center size-14 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white group-hover:bg-white/20 transition-colors group-focus-visible:ring-2 group-focus-visible:ring-white/60">
        {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </span>
      <span className="text-xs font-medium text-white/70">{label}</span>
    </button>
  );
}

export const SlideshowAudioControl = memo(SlideshowAudioControlImpl);
